import { useGetTaskQuery } from '../tasks/tasks.api';
import {
  ArrowPathIcon,
  CheckCircleIcon,
  EllipsisHorizontalCircleIcon,
  ExclamationCircleIcon,
} from '@heroicons/react/24/outline';
import styles from './archive-unpacking-modal.module.css';
import { useAppDispatch } from '../../app/hooks';
import { close } from '../modal/modalSlice';
import { Button } from 'shared-components';
import { useState } from 'react';
import { IUnpackingTaskPayload } from '@topshots/core-models';

export const ArchiveUnpackingModal = ({ taskId }: { taskId: string }) => {
  const [interval, setInterval] = useState<undefined | number>(1000);
  const { data } = useGetTaskQuery(taskId, {
    pollingInterval: interval,
  });

  const dispatch = useAppDispatch();

  const payload: IUnpackingTaskPayload = data?.payload ?? {
    totalFiles: 0,
    totalAlbums: 0,
    stages: {},
  };

  return (
    <>
      <ul className={styles.list}>
        <WorkingItem
          status={(payload?.totalAlbums ?? 0) === 0 ? 'pending' : 'complete'}
          content={`Total albums: ${payload?.totalAlbums ?? '...'}`}
        />
        <WorkingItem
          status={(payload?.totalFiles ?? 0) === 0 ? 'pending' : 'complete'}
          content={`Total images: ${payload?.totalFiles ?? '...'}`}
        />
        {Object.keys(payload.stages)
          .map((stageKey) => {
            const stage =
              payload.stages[stageKey as keyof typeof payload.stages];
            return {
              content: `${stage.name}: ${stage.progress}%`,
              index: stage.i,
              status: stage.status,
            };
          })
          .sort((a, b) => a.index - b.index)
          .map((s, i) => (
            <WorkingItem status={s.status} content={s.content} key={i} />
          ))}
      </ul>
      {(payload?.totalFiles ?? 0) > 0 &&
        !Object.keys(payload.stages)
          .map((sKey) => payload.stages[sKey as keyof typeof payload.stages])
          .find((stage) => stage.status !== 'complete') && (
          <div>
            <Button
              onClick={() => {
                setInterval(undefined);
                dispatch(close());
              }}
            >
              Done
            </Button>
          </div>
        )}
    </>
  );
};

export const WorkingItem = ({
  status,
  content,
}: {
  status: 'pending' | 'running' | 'complete' | 'error';
  content: string;
}) => (
  <li className={styles.item}>
    {status === 'running' && (
      <div className={`${styles.icon} ${styles.icon__working}`}>
        <ArrowPathIcon style={{ height: 24 }} />
      </div>
    )}
    {status === 'complete' && (
      <div className={`${styles.icon} ${styles.icon__success}`}>
        <CheckCircleIcon style={{ height: 24 }} />
      </div>
    )}
    {status === 'pending' && (
      <div className={`${styles.icon} ${styles.icon__pending}`}>
        <EllipsisHorizontalCircleIcon style={{ height: 24 }} />
      </div>
    )}
    {status === 'error' && (
      <div className={`${styles.icon} ${styles.icon__error}`}>
        <ExclamationCircleIcon style={{ height: 24 }} />
      </div>
    )}
    {content}
  </li>
);
